import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { useMutation } from '@tanstack/react-query';
import { resetPassword } from '../services/auth';
import { AuthScreen } from '../components/AuthScreen';
import { FormGroup, Label, Input, HelperText, ErrorAlert, AlertBanner, Button } from '../components/styled';
import { MIN_PASSWORD_LENGTH } from '../constants/auth';

const FullButton = styled(Button)`
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const FooterRow = styled.div`
  margin-top: ${({ theme }) => theme.spacing(6)};
  font-size: ${({ theme }) => theme.font.size.sm};
`;

const BackLink = styled(Link)`
  color: ${({ theme }) => theme.accent.primary};
  text-decoration: none;
  font-weight: ${({ theme }) => theme.font.weight.medium};

  &:hover {
    text-decoration: underline;
  }
`;

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => resetPassword(token, newPassword),
    onError: (err: any) =>
      setError(err.response?.data?.error || 'Could not reset your password. The link may have expired.'),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    mutation.mutate();
  };

  // No token in the URL → the link is malformed or was opened directly.
  if (!token) {
    return (
      <AuthScreen title="Reset link invalid">
        <ErrorAlert>This password reset link is missing or invalid. Please request a new one.</ErrorAlert>
        <FooterRow>
          <BackLink to="/forgot-password">Request a new link</BackLink>
        </FooterRow>
      </AuthScreen>
    );
  }

  return (
    <AuthScreen title="Choose a new password" subtitle="Enter a new password for your account.">
      {mutation.isSuccess ? (
        <>
          <AlertBanner variant="success">Your password has been reset.</AlertBanner>
          <FullButton type="button" onClick={() => navigate('/login')}>
            Continue to sign in
          </FullButton>
        </>
      ) : (
        <form onSubmit={handleSubmit}>
          {error && <ErrorAlert>{error}</ErrorAlert>}
          <FormGroup>
            <Label htmlFor="newPassword">New Password</Label>
            <Input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              minLength={MIN_PASSWORD_LENGTH}
              required
            />
            <HelperText>At least {MIN_PASSWORD_LENGTH} characters.</HelperText>
          </FormGroup>
          <FormGroup>
            <Label htmlFor="confirmPassword">Confirm New Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
          </FormGroup>
          <FullButton type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Resetting...' : 'Reset password'}
          </FullButton>
          <FooterRow>
            <BackLink to="/login">Back to sign in</BackLink>
          </FooterRow>
        </form>
      )}
    </AuthScreen>
  );
}

export default ResetPassword;
